import { useMemo } from 'react';
import { Link } from '@backstage/ui';
import {
  SEVERITY_COLORS,
  severityForeground,
} from '@internal/backstage-plugin-catalog-devops-common';
import {
  ModernRightDrawer,
  filterCloudFields,
} from '@internal/backstage-plugin-devops-react';
import { useCloudResourcesContext } from '../Providers';
import { getConsoleUrl, isHttpUrl } from '../CloudConsoleLink';
import { RegionChip } from '../RegionChip';
import s from './short-info.module.css';

/**
 * Resource quick-view drawer 1:1 with design/resources-page-mockup.html:
 * kicker + name + badge head, Compliance (failed-rule pills), Inventory
 * meta tiles, cloud Attributes in the key-value table and a foot with the
 * full resource page link.
 */
export function ShortInfoResourceDrawer() {
  const { selectedResource, unselectResource, rules, failedRulesByResource } =
    useCloudResourcesContext();

  const closeShortInfo = () => unselectResource();

  const records = useMemo(
    () => (selectedResource ? filterCloudFields(selectedResource) : {}),
    [selectedResource],
  );

  const cloudFieldsData = useMemo(
    () =>
      Object.keys(records).map(key => ({
        key,
        value: (records as Record<string, unknown>)[key],
      })),
    [records],
  );

  const failedRules = selectedResource
    ? (failedRulesByResource[selectedResource.metadata.name] ?? [])
    : [];

  const consoleUrl = selectedResource
    ? getConsoleUrl(selectedResource)
    : undefined;

  const ruleTitle = (ruleName: string) => {
    const rule = rules.find(candidate => candidate.metadata.name === ruleName);
    return rule?.metadata.title ?? ruleName;
  };

  const header = selectedResource ? (
    <div className={s.head}>
      <div className={s.headMain}>
        <span className={s.kicker}>Resource details</span>
        <h2 className={s.headTitle}>
          {selectedResource.metadata.title ?? selectedResource.metadata.name}
        </h2>
        <div className={s.badges}>
          {selectedResource.spec?.service && (
            <span className={s.badge}>{selectedResource.spec.service}</span>
          )}
          <span className={s.badge}>
            <RegionChip region={selectedResource.spec?.region ?? ''} />
          </span>
          {selectedResource.spec?.category && (
            <span className={s.badge}>{selectedResource.spec.category}</span>
          )}
          {selectedResource.spec?.provider && (
            <span className={s.badge}>
              {selectedResource.spec.provider.toLowerCase()}
            </span>
          )}
        </div>
      </div>
    </div>
  ) : undefined;

  const footer = selectedResource ? (
    <div className={s.foot}>
      <Link
        className={s.footLink}
        href={`/catalog/${
          selectedResource.metadata.namespace ?? 'default'
        }/cloudresource/${selectedResource.metadata.name}`}
        target="_blank"
        rel="noopener noreferrer"
      >
        Open full resource page ↗
      </Link>
    </div>
  ) : undefined;

  return (
    <ModernRightDrawer
      title={selectedResource?.spec.type ?? ''}
      header={header}
      footer={footer}
      isOpen={selectedResource !== undefined}
      onClose={closeShortInfo}
      width={520}
    >
      {selectedResource && (
        <>
          <div className={s.secTitle}>Compliance</div>
          {failedRules.length ? (
            <div>
              {failedRules.map(failedRule => (
                <div key={failedRule.ruleName} className={s.ruleRow}>
                  <span
                    className={s.pill}
                    style={{
                      backgroundColor:
                        SEVERITY_COLORS[failedRule.severity] ?? '#a1a1a1',
                      color: severityForeground(failedRule.severity),
                    }}
                  >
                    {failedRule.severity}
                  </span>
                  <Link
                    className={s.ruleRowLink}
                    href={`/catalog/default/rule/${failedRule.ruleName}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={ruleTitle(failedRule.ruleName)}
                  >
                    {ruleTitle(failedRule.ruleName)}
                  </Link>
                </div>
              ))}
            </div>
          ) : (
            <p className={s.desc}>No failed rules for this resource.</p>
          )}

          <div className={s.secTitle}>Inventory</div>
          <div className={s.metaGrid}>
            <div>
              <div className={s.k}>Service</div>
              <div className={s.v}>{selectedResource.spec?.service ?? '—'}</div>
            </div>
            <div>
              <div className={s.k}>Region</div>
              <div className={s.v}>
                <RegionChip region={selectedResource.spec?.region ?? ''} />
              </div>
            </div>
            <div>
              <div className={s.k}>Category</div>
              <div className={s.v}>{selectedResource.spec?.category ?? '—'}</div>
            </div>
            <div>
              <div className={s.k}>Provider</div>
              <div className={s.v}>
                {selectedResource.spec?.provider?.toLowerCase() ?? '—'}
              </div>
            </div>
            {consoleUrl && (
              <div>
                <div className={s.k}>Cloud console</div>
                <div className={s.v}>
                  <Link
                    href={consoleUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Open in{' '}
                    {selectedResource.spec?.provider?.toUpperCase()} console ↗
                  </Link>
                </div>
              </div>
            )}
          </div>

          <div className={s.secTitle}>Attributes</div>
          {cloudFieldsData.length ? (
            <table className={s.kv}>
              <tbody>
                {cloudFieldsData.map(entry => (
                  <tr key={entry.key}>
                    <td className={s.key}>{entry.key}</td>
                    <td className={s.val}>
                      {isHttpUrl(entry.value) ? (
                        <a
                          href={String(entry.value)}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {String(entry.value)}
                        </a>
                      ) : (
                        String(entry.value ?? '')
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className={s.desc}>No cloud attributes recorded.</p>
          )}
        </>
      )}
    </ModernRightDrawer>
  );
}
